import styles from './AlertEngine.module.scss';
import activityStyles from '../_shared/ActivityCard.module.scss';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import BottomDrawer from '../../../../common/_custom/BottomDrawer/BottomDrawer';
import { getAlertNumber } from '../../../../store/dashboard/AlertEngine/alertEngine_apiHelpers';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { getTimeStamp } from '../../../../utils/dateUtils';
import AlertEngineTable from './components/AlertEngineTable/AlertEngineTable';
import { Mixpanel } from '../../../../utils/mixpanel';
import { INVESTOR_CLICK_ALERTS, INVESTOR_CLOSE_ALERTS } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { alertTriangleIcon } from '../../../../assets/hostedassets';
import ActivityCountPill from '../_shared/ActivityCountPill';
import { useIsMobile } from '../../../../common/_custom/Hooks/useIsMobile';

interface AlertEngineProps {
    selectedCompany: any;
    currentTab: string;
    variant?: 'sidebar' | 'card';
}

const AlertEngine = ({ selectedCompany, currentTab, variant = 'sidebar' }: AlertEngineProps) => {
    const [showAlerts, setShowAlerts] = useState(false);
    const [alertCount, setAlertCount] = useState('');
    const [isAlertGenerated, setAlertGenerated] = useState<boolean>(false);
    const params = useParams();
    const user = useAppSelector((state) => state.user);
    // Desktop keeps the live card design; mobile renders the redesigned card.
    const isMobile = useIsMobile();

    const alertPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const alertPollingCount = useRef(0);

    useEffect(() => {
        getAlertsNumber();

        return () => {
            stopPolling();
        };
    }, []);

    const startPolling = () => {
        if (!alertPollingRef.current) {
            alertPollingRef.current = setInterval(() => {
                getAlertsNumber();
            }, 10000);
        }
    };

    const stopPolling = () => {
        if (alertPollingRef.current) {
            clearInterval(alertPollingRef.current);
            alertPollingRef.current = null;
        }
    };

    const getAlertsNumber = async () => {
        alertPollingCount.current += 1;
        const payload = {
            assesseOrgId: params?.id,
            parentOrgId: user?.userData?.investorOrgId,
            requestId: getTimeStamp(),
        };
        const res = await getAlertNumber(payload);
        if (res) {
            setAlertCount(res?.alertCount);
            if (!res?.alertsGenerated) {
                setAlertGenerated(false);
                if (alertPollingCount.current < 9) {
                    startPolling();
                } else stopPolling();
            } else {
                setAlertGenerated(true);
                stopPolling();
            }
        } else {
            stopPolling();
        }
    };

    const onAlertsButtonClick = () => {
        setShowAlerts(true);
    };

    const registerMixpanelEvent = () => {
        Mixpanel.track(INVESTOR_CLICK_ALERTS, {
            Timestamp: getTimeStamp(),
            AICASection: selectedCompany?.companyAccessType,
            TabName: currentTab,
            source: variant,
        });
    };

    useEffect(() => {
        if (showAlerts) registerMixpanelEvent();
    }, [showAlerts]);

    const isZeroAlerts = !alertCount || alertCount === '0';

    // Sidebar row: label + the shared count pill. Same button and the same zero-state
    // (grey pill, not clickable) as the Overview card, but static — no hover swap, so
    // the count stays put and the row reads identically on every device.
    const renderSidebarVariant = () => (
        <button type="button" className={activityStyles.SidebarChip} onClick={onAlertsButtonClick} disabled={isZeroAlerts}>
            <span className={activityStyles.SidebarLabel} title="EWS Alerts">EWS Alerts</span>
            <ActivityCountPill tone="alerts" count={isAlertGenerated ? alertCount || '0' : '0'} muted={isZeroAlerts} compactArrow />
        </button>
    );

    // Mobile: redesigned card (title row on top + full-width colored count row).
    const renderCardMobile = () => (
        <button
            type="button"
            className={clsx(activityStyles.AlertsSummaryCard, activityStyles.MobileCard, { [activityStyles.Disabled]: isZeroAlerts })}
            onClick={onAlertsButtonClick}
            disabled={isZeroAlerts}
        >
            <div className={activityStyles.CardHeader}>
                <img src={alertTriangleIcon} alt="" className={activityStyles.Icon} />
                <span className={activityStyles.Title}>Alerts</span>
            </div>
            <ActivityCountPill tone="alerts" count={isAlertGenerated ? alertCount || '0' : '0'} muted={isZeroAlerts} fullWidth />
        </button>
    );

    // Desktop: the live card design (icon + title + description, right-side count/hover-view).
    const renderCardDesktop = () => (
        <div
            className={clsx(activityStyles.AlertsSummaryCard, { [activityStyles.Disabled]: isZeroAlerts })}
            onClick={isZeroAlerts ? undefined : onAlertsButtonClick}
        >
            <div className={activityStyles.Left}>
                <div className={activityStyles.LeftTop}>
                    <img src={alertTriangleIcon} alt="" className={activityStyles.Icon} />
                    <span className={activityStyles.Title}>Alerts</span>
                </div>
                <span className={activityStyles.Description}>Alerts for Company &amp; Directors</span>
            </div>
            <ActivityCountPill
                tone="alerts"
                count={isAlertGenerated ? alertCount || '0' : '0'}
                muted={isZeroAlerts}
                hoverText={isZeroAlerts ? 'No alerts' : 'View alerts'}
            />
        </div>
    );

    const renderCardVariant = () => (isMobile ? renderCardMobile() : renderCardDesktop());

    const closeAlertsDrawer = () => {
        Mixpanel.track(INVESTOR_CLOSE_ALERTS, {
            Timestamp: getTimeStamp(),
            AICASection: selectedCompany?.companyAccessType,
            TabName: currentTab,
        });
        setShowAlerts(false);
    };

    const alertsDrawerContent = (
        <>
            <div className={styles.AlertDrawerHeader}>
                <h1>Alerts</h1>
            </div>
            <div style={{ height: '100%' }}>
                <AlertEngineTable
                    assesseOrgId={params?.id ?? ''}
                    parentOrgId={user?.userData?.investorOrgId}
                    assesseeDisplayName={selectedCompany?.orgName}
                />
            </div>
        </>
    );

    return (
        <>
            {variant === 'card' ? renderCardVariant() : renderSidebarVariant()}
            {showAlerts &&
                (isMobile ? (
                    <BottomDrawer allowClose heading="Alerts" onClose={closeAlertsDrawer} height="85%">
                        {alertsDrawerContent}
                    </BottomDrawer>
                ) : (
                    <SideDrawer allowClose heading="Alerts" onClose={closeAlertsDrawer} width="80%">
                        {alertsDrawerContent}
                    </SideDrawer>
                ))}
        </>
    );
};
export default AlertEngine;
